import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { AppStore } from '../state/store';
import { SceneHost } from '../render/scene';
import { Overlays, OverlayPick } from '../render/overlays';
import {
  appendNode,
  appendTemplate,
  bankNodesFromSnapshot,
  deleteNodes,
  insertNodeOnCurve,
  nodeAxes,
  selectAdjacentNode,
} from '../state/commands';
import { Tool, flashHint } from './toolbar';

/** Bumps whenever a new Editor mounts — drops stale Vite-HMR listeners. */
let inputEpoch = 0;
let inputAbort: AbortController | null = null;
/** Previous Editor's dispose — Vite HMR can remount without a full reload. */
let disposePreviousEditor: (() => void) | null = null;

/**
 * 3D editing: pick node spheres and drag them with a translate gizmo, turn the
 * selection with rotation rings about a pivot, click-to-append, double-click
 * the track to insert a node. The curve always smoothly interpolates nodes
 * (C² spline); add points for sharper corners.
 *
 * Ring colours follow the track frame: red = twist/bank about the tangent,
 * green = yaw, blue = pitch. Red writes the roll channel (rails twist, heartline
 * stays); green/blue move control points.
 */
export class Editor {
  tool: Tool = 'select';
  buildHeight = 3;
  /** When true, translate-gizmo drags snap node positions to `snapSize`. */
  snapEnabled = false;
  snapSize = 5;
  /** When true, XYZ labels stay visible on every control point. */
  showAllCoords = false;
  /** When true (e.g. view-train POV), hide all coordinate tooltips. */
  suppressCoordTips = false;
  /**
   * Optional early pointer handler (e.g. print-chunk / build-cube pick).
   * - `true`: consume the event (stopPropagation) and treat as a hit
   * - `'passthrough-hit'`: treat as a hit (don't clear selection) but let
   *   TransformControls / OrbitControls still receive the event
   * - `false`: ignore
   */
  earlyPointerDown: ((e: PointerEvent) => boolean | 'passthrough-hit') | null = null;
  /**
   * Optional early key handler (e.g. Delete on a selected support stub).
   * Return true to consume the event.
   */
  earlyKeyDown: ((e: KeyboardEvent) => boolean) | null = null;
  /**
   * Fired when a track node is picked or the background clears the selection
   * (so other viewport tools can drop their own selection).
   */
  onViewportSelect: (() => void) | null = null;

  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private gizmo: TransformControls; // translate arrows
  private rotGizmo: TransformControls; // rotation rings
  private pivot = new THREE.Object3D(); // what the rings turn: selection centroid, track-aligned
  private dragTarget: { kind: 'node'; nodeId: string } | null = null;
  private pivotStartQuat = new THREE.Quaternion();
  private rotateStartNodes: { id: string; p: THREE.Vector3; hIn: THREE.Vector3; hOut: THREE.Vector3 }[] = [];
  private rotateStartBanks = new Map<string, number | undefined>();
  /** World positions of selected nodes at translate-drag start (for free multi-move). */
  private translateStartPositions = new Map<string, THREE.Vector3>();
  private translateLeaderStart = new THREE.Vector3();
  private downPos = { x: 0, y: 0 };
  /** True if a gizmo drag started this gesture — skip selection on pointerup. */
  private gizmoDragged = false;
  /**
   * Gesture pick state from OUR pointerdown. Stale HMR listeners must not clear
   * a selection made by a newer Editor instance on pointerup.
   */
  private downPick: 'hit' | 'miss' | 'none' = 'none';
  private orbitWasEnabledOnPick = true;
  private readonly epoch: number;
  private dragTip: HTMLDivElement;
  private tipLayer: HTMLDivElement;
  private allTips: HTMLDivElement[] = [];
  private readonly tipProj = new THREE.Vector3();

  constructor(
    private store: AppStore,
    private host: SceneHost,
    private overlays: Overlays,
    private getTrackMeshes: () => THREE.Object3D[],
  ) {
    // Drop canvas listeners + gizmos from any previous Editor (Vite HMR).
    disposePreviousEditor?.();
    inputAbort?.abort();
    inputAbort = new AbortController();
    this.epoch = ++inputEpoch;
    const { signal } = inputAbort;

    this.gizmo = new TransformControls(host.camera, host.renderer.domElement);
    this.gizmo.setSize(0.8);
    this.gizmo.setMode('translate');
    this.gizmo.enabled = false;
    host.scene.add(this.gizmo.getHelper());

    // rings sit outside the arrow tips so both stay clickable on the same node
    this.rotGizmo = new TransformControls(host.camera, host.renderer.domElement);
    this.rotGizmo.setSize(1.25);
    this.rotGizmo.setMode('rotate');
    this.rotGizmo.setSpace('local'); // rings follow the track frame: twist / yaw / pitch
    this.rotGizmo.enabled = false;
    host.scene.add(this.rotGizmo.getHelper());
    host.scene.add(this.pivot);

    this.dragTip = document.createElement('div');
    this.dragTip.id = 'drag-coord-tip';
    this.dragTip.classList.add('hidden');
    const wrap = document.getElementById('viewport-wrap')!;
    wrap.appendChild(this.dragTip);

    this.tipLayer = document.createElement('div');
    this.tipLayer.id = 'node-coord-tips';
    wrap.appendChild(this.tipLayer);

    disposePreviousEditor = () => {
      this.hideDragTip();
      this.dragTip.remove();
      this.tipLayer.remove();
      this.allTips = [];
      this.gizmo.enabled = false;
      this.rotGizmo.enabled = false;
      this.gizmo.detach();
      this.rotGizmo.detach();
      host.scene.remove(this.gizmo.getHelper());
      host.scene.remove(this.rotGizmo.getHelper());
      host.scene.remove(this.pivot);
      this.gizmo.dispose();
      this.rotGizmo.dispose();
    };

    this.gizmo.addEventListener('dragging-changed', (e) => {
      const dragging = (e as any).value as boolean;
      if (dragging) {
        this.gizmoDragged = true;
        this.rotGizmo.enabled = false;
        host.orbit.enabled = false;
        this.store.beginGesture();
        this.captureTranslateStarts();
        const obj = (this.gizmo as any).object as THREE.Object3D | undefined;
        if (obj && !this.showAllCoords) this.updateDragTip(obj.position);
      } else {
        this.translateStartPositions.clear();
        this.store.endGesture();
        this.restoreGizmoEnabled();
        host.orbit.enabled = true;
        this.hideDragTip();
      }
    });
    this.gizmo.addEventListener('objectChange', () => this.onGizmoChange());

    this.rotGizmo.addEventListener('dragging-changed', (e) => {
      const dragging = (e as any).value as boolean;
      if (dragging) {
        this.gizmoDragged = true;
        this.gizmo.enabled = false;
        host.orbit.enabled = false;
        this.onRotateDragStart();
      } else {
        this.store.endGesture();
        this.restoreGizmoEnabled();
        host.orbit.enabled = true;
        this.syncGizmo(); // re-align the rings with the shape they just made
      }
    });
    this.rotGizmo.addEventListener('objectChange', () => this.onRotateChange());

    const canvas = host.renderer.domElement;
    canvas.style.touchAction = 'none';

    // Capture phase: screen-space pick beats TransformControls / OrbitControls.
    canvas.addEventListener(
      'pointerdown',
      (e) => {
        if (this.epoch !== inputEpoch) return;
        this.downPos = { x: e.clientX, y: e.clientY };
        this.gizmoDragged = false;
        this.downPick = 'none';
        if (this.store.mode !== 'build') return;
        if (this.tool !== 'select' && this.tool !== 'rotate') return;
        if (e.button !== 0) return;

        // Disable gizmos before overlay pick so their invisible pickers cannot capture.
        this.gizmo.enabled = false;
        this.rotGizmo.enabled = false;
        this.host.orbit.update();
        this.setPointer(e);

        // Node spheres render with depthTest:false (always on top). Pick them
        // before stubs / feet / print chunks so those cannot steal the click.
        const hit = this.overlays.pickAtScreen(this.host.camera, e.clientX, e.clientY, canvas);
        if (hit) {
          this.orbitWasEnabledOnPick = host.orbit.enabled;
          host.orbit.enabled = false;
          this.applyOverlayPick(hit, e.shiftKey || e.ctrlKey || e.metaKey);
          this.gizmo.enabled = false;
          this.rotGizmo.enabled = false;
          this.downPick = 'hit';
          e.stopImmediatePropagation();
          e.preventDefault();
          return;
        }

        this.restoreGizmoEnabled();
        const early = this.earlyPointerDown?.(e);
        if (early === true) {
          this.downPick = 'hit';
          e.stopImmediatePropagation();
          e.preventDefault();
          return;
        }
        if (early === 'passthrough-hit') {
          // e.g. build-cube gizmo: keep selection, but don't steal the event.
          this.downPick = 'hit';
          return;
        }

        this.downPick = 'miss';
        this.restoreGizmoEnabled();
      },
      { capture: true, signal },
    );
    canvas.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
    canvas.addEventListener('dblclick', (e) => this.onDblClick(e), { signal });
    window.addEventListener('keydown', (e) => this.onKey(e), { signal });

    store.on('selection', () => this.syncGizmo());
    store.on('doc', () => this.detach());
    store.on('mode', () => {
      if (store.mode !== 'build') this.detach();
      else this.syncGizmo();
    });
  }

  setTool(tool: Tool): void {
    this.tool = tool;
    if (tool === 'fly' || tool === 'rct') this.detach();
    else this.syncGizmo();
    this.restoreGizmoEnabled();
  }

  private restoreGizmoEnabled(): void {
    if (this.store.mode !== 'build' || this.tool === 'fly' || this.tool === 'rct' || this.tool === 'add') {
      this.gizmo.enabled = false;
      this.rotGizmo.enabled = false;
      return;
    }
    if (this.tool === 'rotate') {
      this.gizmo.enabled = false;
      this.rotGizmo.enabled = this.store.selection.size > 0;
      return;
    }
    // Select: translate only when something is selected. Keeping rotate rings
    // enabled here made their large invisible pickers steal clicks from nodes.
    // Keeping translate enabled with nothing attached still captures pointers.
    this.gizmo.enabled = this.store.selection.size > 0;
    this.rotGizmo.enabled = false;
  }

  private setPointer(e: MouseEvent): void {
    const rect = this.host.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.host.camera);
  }

  /** Apply a screen-space overlay hit: select the node (Shift/Ctrl = multi-select). */
  private applyOverlayPick(hit: OverlayPick, additive: boolean): void {
    this.dragTarget = null;
    this.onViewportSelect?.();
    if (additive) {
      this.store.toggleSelected(hit.nodeId);
      return;
    }
    // Re-clicking a node already in a multi-selection keeps the set and makes
    // that node the translate gizmo anchor (last in selection order).
    if (this.store.selection.has(hit.nodeId) && this.store.selection.size > 1) {
      const rest = [...this.store.selection].filter((id) => id !== hit.nodeId);
      this.store.setSelection([...rest, hit.nodeId]);
      return;
    }
    this.store.setSelection([hit.nodeId]);
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.epoch !== inputEpoch) return;
    if (this.store.mode !== 'build') return;
    if (this.tool === 'fly' || this.tool === 'rct') return; // owned by FlyMode / RctMode

    const downPick = this.downPick;
    this.downPick = 'none';

    // Finished a gizmo drag — don't treat it as a selection click
    if (this.gizmoDragged) {
      this.gizmoDragged = false;
      this.restoreGizmoEnabled();
      return;
    }
    if (this.isDragging()) return;

    // Selection already applied on pointerdown — restore orbit + gizmos.
    if (downPick === 'hit') {
      this.host.orbit.enabled = this.orbitWasEnabledOnPick;
      this.restoreGizmoEnabled();
      return;
    }

    // ignore drags (camera grab-pan / free look)
    if (Math.hypot(e.clientX - this.downPos.x, e.clientY - this.downPos.y) > 4) {
      this.restoreGizmoEnabled();
      return;
    }
    if (e.button !== 0) return;
    this.setPointer(e);

    if (this.tool === 'add') {
      const hit = this.raycaster.intersectObject(this.host.ground, false)[0];
      if (hit) {
        const p = hit.point.clone();
        p.y = this.buildHeight;
        appendNode(this.store, p);
        const nodes = this.store.doc.track.nodes;
        this.store.setSelection([nodes[nodes.length - 1].id]);
      }
      this.restoreGizmoEnabled();
      return;
    }

    // Only clear when THIS instance's pointerdown saw a miss. Prevents stale
    // Vite-HMR Editor listeners from wiping a selection made by a newer instance.
    if (downPick !== 'miss') {
      this.restoreGizmoEnabled();
      return;
    }

    const overlayHit = this.overlays.pickAtScreen(
      this.host.camera,
      e.clientX,
      e.clientY,
      this.host.renderer.domElement,
    );
    if (overlayHit) {
      this.applyOverlayPick(overlayHit, e.shiftKey || e.ctrlKey || e.metaKey);
      this.restoreGizmoEnabled();
      return;
    }
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
      this.onViewportSelect?.();
      this.store.setSelection([]);
    }
    this.restoreGizmoEnabled();
  }

  private onDblClick(e: MouseEvent): void {
    if (this.store.mode !== 'build' || this.tool !== 'select') return;
    this.setPointer(e);
    const hits = this.raycaster.intersectObjects(this.getTrackMeshes(), true);
    const hit = hits.find((h) => h.object.userData.isTrack);
    if (!hit) return;
    // nearest s on the centerline to the clicked point
    const runtime = this.store.runtime;
    let bestS = 0;
    let bestD = Infinity;
    for (const f of runtime.frames) {
      const d = f.railPos.distanceToSquared(hit.point);
      if (d < bestD) {
        bestD = d;
        bestS = f.s;
      }
    }
    const { span, u } = runtime.centerline.sToSpanU(bestS);
    if (u < 0.02 || u > 0.98) return; // too close to an existing node
    const id = insertNodeOnCurve(this.store, span, u);
    if (id) {
      this.store.setSelection([id]);
      flashHint('Node inserted on the curve');
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      e.target instanceof HTMLSelectElement
    )
      return;
    if (e.ctrlKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      this.store.undo();
      return;
    }
    if (e.ctrlKey && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      this.store.redo();
      return;
    }
    // Banking chart owns Delete / arrows while focused
    if (e.target instanceof HTMLElement && e.target.id === 'rollcanvas') return;
    if (this.earlyKeyDown?.(e)) {
      e.preventDefault();
      return;
    }
    if (this.store.mode !== 'build') return;
    if (this.tool === 'fly') return; // Q / WASD handled by FlyMode
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.store.selection.size > 0) deleteNodes(this.store, this.store.selection);
      return;
    }
    if (e.key === 'Insert') {
      e.preventDefault();
      if (this.store.doc.track.closed) {
        flashHint('Uncheck Connect end to start to add pieces at the free end');
        return;
      }
      if (!appendTemplate(this.store, 'straight')) {
        flashHint('Could not add a track piece');
      }
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      selectAdjacentNode(this.store, 1);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      selectAdjacentNode(this.store, -1);
    }
  }

  /** Attach the active tool's gizmo to the selection. */
  private syncGizmo(): void {
    if (this.store.mode !== 'build') return this.detach();
    if (this.tool === 'fly' || this.tool === 'rct' || this.tool === 'add') return this.detach();
    const ids = [...this.store.selection];
    if (ids.length === 0) return this.detach();

    if (this.tool === 'rotate') {
      this.gizmo.detach();
      if (!this.syncRings()) return this.detach();
      this.restoreGizmoEnabled();
      return;
    }

    // Select: translate arrows only — detach unused rotate rings so they never pick.
    this.rotGizmo.detach();
    const lastId = ids[ids.length - 1];
    const obj = this.overlays.meshFor('node', lastId);
    if (!obj) return this.detach();
    this.gizmo.attach(obj);
    this.restoreGizmoEnabled();
  }

  /**
   * Place the rotation rings: centred on the selection, oriented to the track
   * frame at its first node, so the red/green/blue rings read as twist (along
   * the track), yaw (heading) and pitch (nose up/down) instead of world X/Y/Z.
   */
  private syncRings(): boolean {
    const selected = this.store.doc.track.nodes.filter((n) => this.store.selection.has(n.id));
    if (selected.length === 0) return false;

    const centroid = new THREE.Vector3();
    for (const n of selected) centroid.add(n.p);
    this.pivot.position.copy(centroid.divideScalar(selected.length));

    const axes = nodeAxes(selected[0]);
    // (tan, up, right) is right-handed: tan × up = right
    this.pivot.quaternion.setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(axes.roll, axes.yaw, axes.pitch),
    );
    this.rotGizmo.attach(this.pivot);
    return true;
  }

  private detach(): void {
    this.gizmo.detach();
    this.rotGizmo.detach();
    this.dragTarget = null;
  }

  private onRotateDragStart(): void {
    this.store.beginGesture();
    this.pivotStartQuat.copy(this.pivot.quaternion);
    this.rotateStartNodes = this.store.doc.track.nodes
      .filter((n) => this.store.selection.has(n.id))
      .map((n) => ({ id: n.id, p: n.p.clone(), hIn: n.hIn.clone(), hOut: n.hOut.clone() }));
    this.rotateStartBanks = new Map(
      this.store.doc.track.nodes.map((n) => [n.id, n.forcedBankDeg]),
    );
  }

  /** Arc-length span of the current selection — used when the red ring banks the rails. */
  private selectionRange(): { s0: number; s1: number } {
    const idx = this.store.selectedIndices();
    const cl = this.store.runtime.centerline;
    const s0 = cl.spanStartS(idx[0]) ?? 0;
    const s1 = cl.spanStartS(idx[idx.length - 1]) ?? s0;
    return { s0: isFinite(s0) ? s0 : 0, s1: isFinite(s1) ? Math.max(s1, s0) : s0 };
  }

  /**
   * Ring drag: red (local X / tangent) banks the track surface; green/blue turn
   * the control points about the pivot from the captured start state.
   */
  private onRotateChange(): void {
    if (!(this.rotGizmo as any).dragging) return;
    const axis = (this.rotGizmo as any).axis as string | null;

    // Red ring = twist about the track tangent → node bank, not positions.
    if (axis === 'X') {
      const deg = ((this.rotGizmo as any).rotationAngle as number) * (180 / Math.PI);
      const { s0, s1 } = this.selectionRange();
      const cl = this.store.runtime.centerline;
      bankNodesFromSnapshot(
        this.store.doc.track.nodes,
        this.rotateStartBanks,
        (i) => cl.spanStartS(i),
        s0,
        s1,
        deg,
        this.store.runtime.totalLength,
        this.store.doc.track.closed,
        false,
      );
      this.store.updateGesture(this.store.selectedIndices());
      return;
    }

    const delta = this.pivot.quaternion.clone().multiply(this.pivotStartQuat.clone().invert());
    const nodesById = new Map(this.store.doc.track.nodes.map((n) => [n.id, n]));
    for (const start of this.rotateStartNodes) {
      const n = nodesById.get(start.id);
      if (!n) continue;
      n.p.copy(start.p).sub(this.pivot.position).applyQuaternion(delta).add(this.pivot.position);
      n.hIn.copy(start.hIn).applyQuaternion(delta);
      n.hOut.copy(start.hOut).applyQuaternion(delta);
      n.forcedPos = true;
    }
    this.store.updateGesture(this.store.selectedIndices());
  }

  private captureTranslateStarts(): void {
    this.translateStartPositions.clear();
    const obj = (this.gizmo as any).object as THREE.Object3D | undefined;
    if (obj) this.translateLeaderStart.copy(obj.position);
    for (const n of this.store.doc.track.nodes) {
      if (this.store.selection.has(n.id)) this.translateStartPositions.set(n.id, n.p.clone());
    }
  }

  private onGizmoChange(): void {
    if (!(this.gizmo as any).dragging) return;
    const obj = (this.gizmo as any).object as THREE.Object3D | undefined;
    if (!obj) return;

    const data = obj.userData as { kind: 'node'; nodeId: string };
    if (!data.nodeId || data.kind !== 'node') return;
    const idx = this.store.doc.track.nodes.findIndex((n) => n.id === data.nodeId);
    if (idx < 0) return;
    if (this.snapEnabled) this.snapPosition(obj.position);
    this.store.doc.track.nodes[idx].p.copy(obj.position);
    this.store.doc.track.nodes[idx].forcedPos = true;

    const axis = String((this.gizmo as any).axis ?? 'XYZ');
    const syncX = axis.includes('X');
    const syncY = axis.includes('Y');
    const syncZ = axis.includes('Z');
    // Free / screen drag: move the whole selection by the leader's delta.
    // Axis / plane handles: copy the leader's value onto that axis for everyone.
    const freeMove = axis === 'XYZ' || axis === 'E' || axis === 'XYZE' || (!syncX && !syncY && !syncZ);

    const dirty: number[] = [];
    const leader = obj.position;
    if (freeMove) {
      const dx = leader.x - this.translateLeaderStart.x;
      const dy = leader.y - this.translateLeaderStart.y;
      const dz = leader.z - this.translateLeaderStart.z;
      this.store.doc.track.nodes.forEach((n, i) => {
        if (!this.store.selection.has(n.id)) return;
        const start = this.translateStartPositions.get(n.id);
        if (!start) return;
        if (n.id === data.nodeId) {
          dirty.push(i);
          return;
        }
        n.p.set(start.x + dx, start.y + dy, start.z + dz);
        n.forcedPos = true;
        if (this.snapEnabled) this.snapPosition(n.p);
        const mesh = this.overlays.meshFor('node', n.id);
        if (mesh) mesh.position.copy(n.p);
        dirty.push(i);
      });
    } else {
      this.store.doc.track.nodes.forEach((n, i) => {
        if (!this.store.selection.has(n.id)) return;
        if (n.id === data.nodeId) {
          dirty.push(i);
          return;
        }
        if (syncX) n.p.x = leader.x;
        if (syncY) n.p.y = leader.y;
        if (syncZ) n.p.z = leader.z;
        n.forcedPos = true;
        const mesh = this.overlays.meshFor('node', n.id);
        if (mesh) {
          if (syncX) mesh.position.x = leader.x;
          if (syncY) mesh.position.y = leader.y;
          if (syncZ) mesh.position.z = leader.z;
        }
        dirty.push(i);
      });
    }

    this.store.updateGesture(dirty.length > 0 ? dirty : [idx]);
    if (!this.showAllCoords) this.updateDragTip(obj.position);
  }

  /** Snap a world position to the current grid (all axes). */
  private snapPosition(p: THREE.Vector3): void {
    const c = Math.max(0.25, this.snapSize);
    p.x = Math.round(p.x / c) * c;
    p.y = Math.round(p.y / c) * c;
    p.z = Math.round(p.z / c) * c;
  }

  /**
   * Keep coordinate labels in sync with the camera. Call once per frame.
   * Always-on mode labels every node; otherwise only the active drag tip.
   */
  updateCoordLabels(): void {
    if (this.suppressCoordTips) {
      this.hideDragTip();
      this.clearAllCoordTips();
      return;
    }
    if (this.showAllCoords) {
      this.hideDragTip();
      this.syncAllCoordTips();
      return;
    }
    this.clearAllCoordTips();
    // Drag tip is updated from gizmo events when showAllCoords is off.
  }

  private syncAllCoordTips(): void {
    const nodes = this.store.doc.track.nodes;
    while (this.allTips.length < nodes.length) {
      const el = document.createElement('div');
      el.className = 'node-coord-tip hidden';
      this.tipLayer.appendChild(el);
      this.allTips.push(el);
    }

    const canvas = this.host.renderer.domElement;
    const canvasRect = canvas.getBoundingClientRect();
    const wrapRect = this.tipLayer.getBoundingClientRect();
    const ox = canvasRect.left - wrapRect.left;
    const oy = canvasRect.top - wrapRect.top;

    for (let i = 0; i < this.allTips.length; i++) {
      const el = this.allTips[i];
      if (i >= nodes.length) {
        el.classList.add('hidden');
        continue;
      }
      const p = nodes[i].p;
      this.tipProj.copy(p).project(this.host.camera);
      if (
        this.tipProj.z < -1 ||
        this.tipProj.z > 1 ||
        Math.abs(this.tipProj.x) > 1.2 ||
        Math.abs(this.tipProj.y) > 1.2
      ) {
        el.classList.add('hidden');
        continue;
      }
      el.textContent = `X ${fmtCoord(p.x)}  Y ${fmtCoord(p.y)}  Z ${fmtCoord(p.z)}`;
      el.classList.remove('hidden');
      const x = ((this.tipProj.x + 1) / 2) * canvasRect.width + ox;
      const y = ((-this.tipProj.y + 1) / 2) * canvasRect.height + oy;
      el.style.left = `${Math.round(x + 12)}px`;
      el.style.top = `${Math.round(y - 22)}px`;
    }
  }

  private clearAllCoordTips(): void {
    for (const el of this.allTips) el.classList.add('hidden');
  }

  private updateDragTip(p: THREE.Vector3): void {
    if (this.suppressCoordTips) {
      this.hideDragTip();
      return;
    }
    const tip = this.dragTip;
    tip.textContent = `X ${fmtCoord(p.x)}  Y ${fmtCoord(p.y)}  Z ${fmtCoord(p.z)}`;
    tip.classList.remove('hidden');

    this.tipProj.copy(p).project(this.host.camera);
    const canvas = this.host.renderer.domElement;
    const canvasRect = canvas.getBoundingClientRect();
    const wrapRect = tip.parentElement!.getBoundingClientRect();
    const x = ((this.tipProj.x + 1) / 2) * canvasRect.width + (canvasRect.left - wrapRect.left);
    const y = ((-this.tipProj.y + 1) / 2) * canvasRect.height + (canvasRect.top - wrapRect.top);
    tip.style.left = `${Math.round(x + 14)}px`;
    tip.style.top = `${Math.round(y - 28)}px`;
  }

  private hideDragTip(): void {
    this.dragTip.classList.add('hidden');
  }

  private isDragging(): boolean {
    return Boolean((this.gizmo as any).dragging || (this.rotGizmo as any).dragging);
  }

  /** Called by main when overlays rebuild (references may have been replaced). */
  refreshAttachment(): void {
    if (this.isDragging()) return;
    this.syncGizmo();
  }
}

function fmtCoord(v: number): string {
  const r = Math.round(v * 100) / 100;
  return Object.is(r, -0) ? '0' : String(r);
}
